import React, { useEffect, useState } from 'react';
import { useEntityList, useTenant } from '../data/entities';
import { useCan } from '../data/permissions';
import { useAuth } from '../AuthContext';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import AgentLibraryPanel from './AgentLibraryPanel';
import AgentSetup from './AgentSetup';
import AgentEditor, { TOOLABLE } from './AgentEditor';

// Agents tab: create/configure deployable chat agents and grab the embed
// snippet. Tool access is allowlist-per-agent over registry entities marked
// agentTools — permission-level safety, configured here, enforced server-side.
// The per-agent form is AgentEditor (shared with App Builder → Design).

export default function OrgAgents({ org, myRole }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const { docs: agents } = useEntityList('agent', { org: org.id });
  const can = useCan(org, myRole);
  const canEdit = can('agent.create');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const entitled = org.entitlements?.agents !== false;
  const [library, setLibrary] = useState([]);
  const [libErr, setLibErr] = useState('');

  useEffect(() => {
    if (!isBackendConfigured()) return undefined;
    let alive = true;
    apiGet('/agent-library')
      .then((d) => { if (alive) setLibrary(d.roles || []); })
      .catch((err) => { if (alive) setLibErr(err.message); });
    return () => { alive = false; };
  }, []);

  const addRole = async (roleId) => {
    setBusy(true);
    setError('');
    try {
      await apiPost(`/agent-library/${roleId}`, { orgId: org.id, workspaceId: tenant.workspace || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {!entitled && (
        <div className="cs-card" style={{ padding: 14 }}>
          <p className="cs-help" style={{ margin: 0 }}>
            Agents are not included in the current plan — see the Billing tab.
          </p>
        </div>
      )}

      {canEdit && (
        <AgentSetup org={org} user={user} toolable={TOOLABLE} entitled={entitled} />
      )}
      {canEdit && (
        <AgentLibraryPanel
          library={library}
          entitled={entitled}
          busy={busy}
          onAdd={addRole}
          libErr={libErr}
        />
      )}
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}

      {agents && !agents.length && entitled && (
        <div className="cs-empty" style={{ marginTop: 16 }}>
          No agents yet{canEdit ? ' — create one above.' : '.'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
        {(agents || []).map((agent) => (
          <AgentEditor key={agent.id} org={org} agent={agent} canEdit={can('agent.update')} defaultOpen={false} />
        ))}
      </div>
    </div>
  );
}
